import React, { ReactElement } from "react";
import toast, { ToastOptions } from "react-hot-toast";
import { Toast } from "react-hot-toast/src/core/types";
import { useIntl } from "react-intl";
import SuccessIcon from "@/assets/svgs/icons/toast_success.svg";
import ErrorIcon from "@/assets/svgs/icons/toast_error.svg";
import WarningIcon from "@/assets/svgs/icons/toast_warning.svg";
import { CloseIcon } from "@/assets/svgs/icons";


type ToastContent = {
    message: string | ReactElement,
    icon?: string
};

const defaultToastStyle = {
    backgroundColor: "#ffffff",
    boxShadow: "0 0 6px 0 #BDBDBD",
    padding: "6px 2px",
    borderRadius: "6px",
};

const defaultToastOptions: Partial<ToastOptions> = {
    duration: 4000
};

interface ToastMessageProps {
    t: Toast;
    content: ToastContent;
    type?: 'success' | 'error' | 'warning';
}

const ToastMessage: React.FC<ToastMessageProps> = ({ t, content, type }) => {
    const intl = useIntl();
    const { message, icon } = content;
    const isAlert = type === "error" || type === "warning";

    return (
        <div className="w-full flex items-center"
             role={isAlert ? "alert" : "status"}
             aria-live={isAlert ? "assertive" : "polite"}
             data-toast-type={type}
        >
            {icon && (
                <img src={icon}
                     alt=""
                     aria-hidden="true"
                     className="mr-2 size-6"
                />
            )}

            <span className="text-black-500 text-[14px] leading-[1.5] font-[500] mr-4">
                {message}
            </span>

            <button className="min-w-4 cursor-pointer"
                    aria-label={intl.formatMessage({ id: 'b0003' })}
                    onClick={e => {
                        e.stopPropagation();
                        toast.dismiss(t.id);
                    }}
            >
                <CloseIcon className="size-4"
                           aria-hidden="true"
                />
            </button>
        </div>
    );
};

export const successToast = (message: string | ReactElement, options?: ToastOptions) => {
    const { style: customStyle = {}, ...customOptions } = options ?? {};
    return toast((t) => <ToastMessage t={t} content={{ message, icon: SuccessIcon }} type="success" />, {
        style: {
            ...defaultToastStyle,
            ...customStyle
        },
        ...defaultToastOptions,
        ...customOptions,
    });
};

export const infoToast = (message: string | ReactElement, options?: ToastOptions) => {
    const { style: customStyle = {}, ...customOptions } = options ?? {};
    return toast((t) => <ToastMessage t={t} content={{ message }} />, {
        style: {
            ...defaultToastStyle,
            ...customStyle
        },
        ...defaultToastOptions,
        ...customOptions,
    });
};

export const errorToast = (message: string | ReactElement, options?: ToastOptions) => {
    const { style: customStyle = {}, ...customOptions } = options ?? {};
    return toast((t) => <ToastMessage t={t} content={{ message, icon: ErrorIcon }} type="error" />, {
        style: {
            ...defaultToastStyle,
            ...customStyle
        },
        ...defaultToastOptions,
        ...customOptions,
    });
};

export const warningToast = (message: string | ReactElement, options?: ToastOptions) => {
    const { style: customStyle = {}, ...customOptions } = options ?? {};
    return toast((t) => <ToastMessage t={t} content={{ message, icon: WarningIcon }} type="warning" />, {
        style: {
            ...defaultToastStyle,
            ...customStyle
        },
        ...defaultToastOptions,
        ...customOptions,
    });
};
